import React, { useEffect, useState } from "react";
import { Grid, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SelectChangeEvent } from "@mui/material";

import { StyledHorizontalScrollDiv, StyledTable } from "../../../../useGeneralStyles";
import {
  ContainerWithLoader,
  FilterAutocomplete,
  PaginationWithLimit,
  Select,
} from "../../../../components/ui";
import TableBody from "./TableBody";
import TableHead from "./TableHead";
import WithdrawalRequestForm from "../WithdrawalRequestForm";

import usePagination from "../../../../hooks/usePagination";
import { APIFunds } from "../../../../api";
import { IWithdrawalWithAccountInfo, TCompletedStatuses } from "../../../../api/funds/index.types";
import { EWithdrawalStatuses } from "../../../../constants";
import { WITHDRAWAL_FILTERS, WITHDRAWAL_STATUSES_OPTIONS } from "../../../Withdrawals/constants";
import { FUNDS_API_URLS } from "../../../../api/funds";

const WithdrawalsRequestList = (): React.ReactElement => {
  const controller = new AbortController();
  const { signal } = controller;

  const params: { id: string } = useParams();
  const id = +params.id;
  const { count, limit, setLimit, setPage, page, setCount } = usePagination();

  const [withdrawals, setWithdrawals] = useState<IWithdrawalWithAccountInfo[]>([]);
  const [editingWithdrawal, setEditingWithdrawal] =
    useState<Omit<IWithdrawalWithAccountInfo, "traded_account">>();
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState({
    by: "initialization_date",
    order: false,
  });
  const [open, setOpen] = useState(false);

  const [search, setSearch] = useState({
    status: EWithdrawalStatuses.pending,
  });

  const handleChangeFilter = (event: SelectChangeEvent<string>) => {
    setSearch({
      ...search,
      status: event.target.value as EWithdrawalStatuses,
    });
  };

  const fetchWithdrawalsList = useQuery({
    queryKey: [FUNDS_API_URLS.withdrawal_list, id, sortBy, limit, page, search],
    queryFn: async () => {
      setIsLoading(true);
      const response = await APIFunds.getWithdrawalsListRequest(
        {
          accounts_id: id,
          limit: +limit,
          page,
          sortBy,
          search,
          activeFilters: {},
        },
        signal
      );
      setWithdrawals(response.data.withdrawals.rows);
      setCount(response.data.withdrawals.count);
      setIsLoading(false);
      return response;
    },
  });

  useEffect(() => {
    if (fetchWithdrawalsList.isError && fetchWithdrawalsList.error) {
      setIsLoading(false);
    }
  }, [fetchWithdrawalsList.isError, fetchWithdrawalsList.error]);

  useEffect(() => {
    (async () => {
      if (editingWithdrawal && !open) {
        await fetchWithdrawalsList.refetch();
      }
    })();
  }, [editingWithdrawal, open]);

  const handleChangeWithdrawalStatus = async (
    e: React.MouseEvent,
    withdrawalId: number,
    status: TCompletedStatuses
  ) => {
    try {
      e.stopPropagation();
      await APIFunds.updateWithdrawalStatus({ id: withdrawalId, status, accounts_id: id });
      await fetchWithdrawalsList.refetch();
    } catch (error) {
      console.error("Error changing withdrawal status", error);
    }
  };

  const handleEditWithdrawal = (withdrawal: IWithdrawalWithAccountInfo) => {
    setOpen(true);
    const { traded_account, ...rest } = withdrawal;
    setEditingWithdrawal(rest);
  };
  const handleCancel = () => setOpen(false);

  return (
    <div>
      <Dialog open={open} onClose={handleCancel}>
        <DialogTitle>Edit withdrawal request</DialogTitle>
        <DialogContent dividers>
          <WithdrawalRequestForm
            accounts_id={id}
            editValues={editingWithdrawal}
            onClose={handleCancel}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} size="small">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <b>Withdrawals requests list</b>
        </Grid>
        <Grid item xs={3} md={3}>
          <Select
            inputLabel="Status"
            label="Status"
            value={search.status}
            onChange={handleChangeFilter}
            options={WITHDRAWAL_STATUSES_OPTIONS}
          />
        </Grid>
        <Grid item xs={9} md={9}>
          <FilterAutocomplete
            search={search}
            setSearch={setSearch}
            filters={WITHDRAWAL_FILTERS}
            inputTypes={{}}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <StyledHorizontalScrollDiv>
            <ContainerWithLoader
              report={withdrawals}
              isLoading={isLoading}
              noResultsMessage="There are no withdrawals"
            >
              <StyledTable>
                <TableHead sortBy={sortBy} setSortBy={setSortBy} />
                <TableBody
                  withdrawals={withdrawals}
                  handleChangeWithdrawalStatus={handleChangeWithdrawalStatus}
                  handleEditWithdrawal={handleEditWithdrawal}
                />
              </StyledTable>
            </ContainerWithLoader>
          </StyledHorizontalScrollDiv>
          <div>
            <PaginationWithLimit
              count={count}
              limit={limit}
              page={page}
              setLimit={setLimit}
              setPageState={setPage}
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default WithdrawalsRequestList;
